// ============================================================
// 🔒 MÍDIAS COM PROPÓSITO — LOCKED COLOR + SKIN SYSTEM
// Last modified: 2026-05-02 by Giovanni Barcelos / MidiaHub
// DO NOT MODIFY WITHOUT EXPLICIT AUTHORIZATION FROM GIOVANNI
// ============================================================

import { IMAGES } from '@/assets/images';

// ─── Route paths ─────────────────────────────────────────────────────────────
export const ROUTE_PATHS = { HOME: '/' };

// ─── Types ───────────────────────────────────────────────────────────────────
export interface SkinConfig {
  id: string;
  bg: string;
  ink: string;
  subtleBg: string;
  card: string;
  headerLogo: string;
  footerLogo: string;
}

export interface Case {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  tags: string[];
}

export interface Testimonial {
  id: string;
  name: string;
  role: string;
  text: string;
  photo: string;
}

export interface ProcessStep {
  id: string;
  title: string;
  description: string;
}

export interface MarketingCard {
  id: string;
  tag: string;
  title: string;
  description: string;
}

// ─── Navigation ──────────────────────────────────────────────────────────────
export const NAV_ITEMS = [
  { label: 'Manifesto', href: '#manifesto' },
  { label: 'Como Trabalhamos', href: '#como-trabalhamos' },
  { label: 'Cases', href: '#cases' },
  { label: 'Trajetória', href: '#trajetoria' },
  { label: 'Marketing Consciente', href: '#marketing-consciente' },
];

// ─── 🔒 5-SKIN SYSTEM — LOCKED 2026-05-02 ────────────────────────────────────
// ORDER IS FIXED. COLORS ARE FINAL. LOGOS ARE PAIRED PER SKIN.
// DO NOT REORDER, RENAME, OR CHANGE hex values without Giovanni's sign-off.
//
// s1 (DEFAULT) : White    #FFFFFF  + Purple #524696   ← site loads on this
// s2           : Cyan     #39fffa  + Purple #524696
// s3           : Purple   #524696  + Cyan   #00e5e0
// s4           : Yellow   #fcdd7c  + Purple #524696
// s5           : Orange   #e35f38  + Cyan   #00e5e0
// ─────────────────────────────────────────────────────────────────────────────
export const SKIN_CONFIGS: SkinConfig[] = [
  {
    // 🔒 s1 — DEFAULT — White + Purple
    id: 's1',
    bg: '#FFFFFF',
    ink: '#524696',
    subtleBg: '#f4f2fa',
    card: '#ede9f7',
    headerLogo: IMAGES.SIMBOLO_01,   // white bg, purple M symbol
    footerLogo: IMAGES.LOGOTIPO_01,  // white bg, purple logotipo
  },
  {
    // 🔒 s2 — Cyan + Purple
    id: 's2',
    bg: '#39fffa',
    ink: '#524696',
    subtleBg: '#00e3de',
    card: '#b3fffe',
    headerLogo: IMAGES.SIMBOLO_04,   // cyan bg, purple M symbol
    footerLogo: IMAGES.LOGOTIPO_02,  // cyan bg, purple logotipo
  },
  {
    // 🔒 s3 — Purple + Cyan
    id: 's3',
    bg: '#524696',
    ink: '#39fffa',
    subtleBg: '#3d3475',
    card: '#443880',
    headerLogo: IMAGES.SIMBOLO_02,   // purple bg, cyan M symbol
    footerLogo: IMAGES.LOGOTIPO_03,  // purple bg, cyan logotipo
  },
  {
    // 🔒 s4 — Yellow + Purple
    id: 's4',
    bg: '#fcdd7c',
    ink: '#524696',
    subtleBg: '#f5d050',
    card: '#fdeea0',
    headerLogo: IMAGES.SIMBOLO_05,   // yellow bg, purple M symbol
    footerLogo: IMAGES.LOGOTIPO_01,  // white/transparent bg, purple logotipo
  },
  {
    // 🔒 s5 — Orange + Cyan
    id: 's5',
    bg: '#e35f38',
    ink: '#39fffa',
    subtleBg: '#c94f2a',
    card: '#c94f2a',
    headerLogo: IMAGES.SIMBOLO_03,   // orange bg, cyan M symbol
    footerLogo: IMAGES.LOGOTIPO_03,  // purple bg, cyan logotipo (screen blend)
  },
];

export const SKIN_CONFIG_MAP: Record<string, SkinConfig> = Object.fromEntries(
  SKIN_CONFIGS.map((s) => [s.id, s])
);

// ─── Process steps ───────────────────────────────────────────────────────────
export const PROCESS_STEPS: ProcessStep[] = [
  {
    id: '1',
    title: 'Diagnóstico profundo',
    description: 'Imersão na marca, no mercado e no público para entender o contexto real antes de qualquer ação.',
  },
  {
    id: '2',
    title: 'Arquitetura de posicionamento',
    description: 'Definição clara de propósito, diferencial e território de comunicação da marca.',
  },
  {
    id: '3',
    title: 'Plano de comunicação anual',
    description: 'Organização de canais, campanhas e indicadores com visão estratégica de longo prazo.',
  },
  {
    id: '4',
    title: 'Produção de conteúdo',
    description: 'Criação de conteúdo com intenção: textos, roteiros, artes e campanhas com coerência de voz.',
  },
  {
    id: '5',
    title: 'Gestão e análise',
    description: 'Acompanhamento contínuo de resultados, ajustes de rota e relatórios de impacto.',
  },
];

// ─── Cases ───────────────────────────────────────────────────────────────────
// 🔒 LOCKED 2026-05-02 — Encontro é o case em destaque (featured, index 0), demais em grid
export const CASES: Case[] = [
  {
    id: 'encontro',
    title: 'Encontro de Culturas Tradicionais da Chapada dos Veadeiros',
    subtitle: 'Comunicação de Evento',
    description: 'Arquitetura de comunicação para um dos maiores encontros culturais do Brasil. Comunicar um projeto cultural complexo envolvendo povos tradicionais, patrocinadores e público diverso.',
    image: "https://images.unsplash.com/photo-1550030712-320c9e746514?w=1080&q=80",
    tags: ['Eventos', 'Cultura', 'Estratégia'],
  },
  {
    id: 'travessia',
    title: 'Travessia',
    subtitle: 'Posicionamento de Marca',
    description: 'Construção de identidade e posicionamento estratégico para marca de moda consciente com presença nacional.',
    image: "https://images.unsplash.com/photo-1596382941523-f18a1ac95f53?w=1080&q=80",
    tags: ['Branding', 'Estratégia', 'Conteúdo'],
  },
  {
    id: 'aldeia',
    title: 'Aldeia',
    subtitle: 'Comunicação Cultural',
    description: 'Estratégia de comunicação para projeto cultural com foco em tradições regionais.',
    image: "https://images.unsplash.com/photo-1767979350591-c0cbba0e52b5?w=1080&q=80",
    tags: ['Cultura', 'Conteúdo'],
  },
  {
    id: 'las-nenas',
    title: 'Las Nenas',
    subtitle: 'Marketing Digital',
    description: 'Gestão de presença digital e estratégia de conteúdo para marca de moda feminina.',
    image: "https://images.unsplash.com/photo-1758613655976-e8c8f85849a2?w=1080&q=80",
    tags: ['Moda', 'Digital'],
  },
  {
    id: 'arvoredo',
    title: 'Arvoredo',
    subtitle: 'Branding & Conteúdo',
    description: 'Desenvolvimento de identidade de marca e estratégia de comunicação para negócio sustentável.',
    image: "https://images.unsplash.com/photo-1555383004-514c5437ec87?w=1080&q=80",
    tags: ['Sustentabilidade', 'Branding'],
  },
];

// ─── Testimonials ─────────────────────────────────────────────────────────────
export const TESTIMONIALS: Testimonial[] = [
  {
    id: 't1',
    name: 'Tatiana Miranda',
    role: 'Empreendedora',
    text: 'A Marina tem uma capacidade única de traduzir a essência de uma marca em comunicação real. Nossa identidade ficou muito mais clara depois do trabalho com ela.',
    photo: IMAGES.TESTIMONIAL_TATIANA,
  },
  {
    id: 't2',
    name: 'Lidi Vilela',
    role: 'Designer',
    text: 'Trabalhar com a Marina é transformador. Ela não entrega apenas estratégia, entrega clareza. Nosso negócio ganhou consistência e presença que não tínhamos antes.',
    photo: IMAGES.TESTIMONIAL_LIDI,
  },
  {
    id: 't3',
    name: 'Ana Ramos',
    role: 'Consultora',
    text: 'A visão estratégica da Marina é impressionante. Ela consegue enxergar além e criar caminhos de comunicação que realmente funcionam para o nosso público.',
    photo: IMAGES.TESTIMONIAL_ANA,
  },
  {
    id: 't4',
    name: 'Flávia Gabriel',
    role: 'Empresária',
    text: 'Com a Marina, entendemos que comunicação consciente não é apenas sobre o que você fala, mas sobre como você se posiciona no mundo. Resultado transformador.',
    photo: IMAGES.TESTIMONIAL_FLAVIA,
  },
  {
    id: 't5',
    name: 'Giovanna Pagliaro',
    role: 'Produtora Cultural',
    text: 'A Marina entende profundamente o universo cultural e sabe como comunicar com autenticidade. Foi essencial para o sucesso do nosso festival.',
    photo: IMAGES.TESTIMONIAL_GIOVANNA,
  },
  {
    id: 't6',
    name: 'Fernando Couto',
    role: 'Diretor Criativo',
    text: 'Objetividade, profundidade e sensibilidade. Esses são os pilares do trabalho da Marina. Recomendo para qualquer marca que queira se comunicar com propósito.',
    photo: IMAGES.TESTIMONIAL_FERNANDO,
  },
];

// ─── Beliefs (manifesto) ──────────────────────────────────────────────────────
// 🔒 LOCKED 2026-05-02 — Crenças da marca conforme briefing final de Marina
export const BELIEFS = [
  'estratégia antes de visibilidade',
  'crescimento com coerência',
  'comunicação responsável',
  'marketing consciente',
];

// ─── Marketing cards ──────────────────────────────────────────────────────────
export const MARKETING_CARDS: MarketingCard[] = [
  {
    id: 'mc1',
    tag: 'Estratégia',
    title: 'Por que marcas conscientes precisam de estratégia?',
    description: 'Ter propósito não é suficiente se a comunicação não traduz esse propósito com clareza para o público certo.',
  },
  {
    id: 'mc2',
    tag: 'Posicionamento',
    title: 'O diferencial invisível que separa marcas comuns de marcas que ficam',
    description: 'Posicionamento não é slogan. É a soma de todas as escolhas que uma marca faz sobre como quer ser percebida.',
  },
  {
    id: 'mc3',
    tag: 'Conteúdo',
    title: 'Conteúdo com intenção: a diferença entre publicar e comunicar',
    description: 'Nem todo conteúdo comunica. Publicar por obrigação não constrói presença — constrói barulho.',
  },
  {
    id: 'mc4',
    tag: 'Cultura',
    title: 'Marketing para o setor cultural: desafios e possibilidades',
    description: 'Comunicar cultura exige sensibilidade especial. O mercado cultural tem suas próprias regras — e suas próprias riquezas.',
  },
];

// ─── Client logos ─────────────────────────────────────────────────────────────
// Excluding solid white square/circle logos: 1,3,4,6,8,10,12,13,14
export const CLIENT_LOGOS = [
  IMAGES.CLIENT_LOGO_2,
  IMAGES.CLIENT_LOGO_5,
  IMAGES.CLIENT_LOGO_7,
  IMAGES.CLIENT_LOGO_9,
  IMAGES.CLIENT_LOGO_11,
  IMAGES.CLIENT_LOGO_15,
];
